import { getDomain } from 'tldts';
import {
  DEFAULT_NOISE_DOMAINS,
  DEFAULT_NOISE_URL_PATH_PREFIXES,
  DEFAULT_NOISE_URL_PATH_SEGMENTS,
  DEFAULT_NOISE_URL_EXTENSIONS,
  DEFAULT_NOISE_URL_SUBSTRINGS,
} from './built-in-noise';
import {
  type ApplicationScopeSource,
  type DomainScopeEntry,
  domainEntryInDimension,
  resolveApplicationScope,
  splitDomainHostAndPath,
} from './domain-scope';
import {
  extractHostname,
  isHostExactlyOrSubdomainOf,
  isRegexPattern,
} from './url-pattern-matching';

export type AssetFilterRules = {
  applicationUrl: string;
  applicationDomains: DomainScopeEntry[];
  noiseDomains: string[];
  noiseUrlPathPrefixes: string[];
  noiseUrlPathSegments: string[];
  noiseUrlExtensions: string[];
  noiseUrlSubstrings: string[];
};

export type BuildAssetFilterRulesInput = ApplicationScopeSource;

export type ApplicationDomainEntry = DomainScopeEntry;

export function buildAssetFilterRules(
  input: BuildAssetFilterRulesInput,
): AssetFilterRules {
  const { applicationUrl } = input;
  const applicationDomains = resolveApplicationScope({
    applicationUrl,
    domains: input.domains,
  });
  const functionalityDomains = applicationDomains.filter((entry) =>
    domainEntryInDimension({ entry, dimension: 'functionality' }),
  );
  const exactEnabledHosts = collectExactEnabledHosts(
    functionalityDomains.map(
      (entry) => splitDomainHostAndPath(entry.domain).host,
    ),
  );
  const applicationPrimaryHosts =
    collectApplicationPrimaryHosts(applicationUrl);
  const noiseDomains = DEFAULT_NOISE_DOMAINS.filter(
    (noiseHost) =>
      !isNoiseHostExcluded({
        noiseHost,
        exactEnabledHosts,
        applicationPrimaryHosts,
      }),
  );

  return {
    applicationUrl,
    applicationDomains,
    noiseDomains,
    noiseUrlPathPrefixes: [...DEFAULT_NOISE_URL_PATH_PREFIXES],
    noiseUrlPathSegments: [...DEFAULT_NOISE_URL_PATH_SEGMENTS],
    noiseUrlExtensions: [...DEFAULT_NOISE_URL_EXTENSIONS],
    noiseUrlSubstrings: [...DEFAULT_NOISE_URL_SUBSTRINGS],
  };
}

function collectExactEnabledHosts(hosts: string[]): Set<string> {
  const set = new Set<string>();
  for (const host of hosts) {
    const normalized = host.toLowerCase();
    if (!normalized || isRegexPattern(normalized)) {
      continue;
    }
    set.add(normalized);
  }
  return set;
}

function collectApplicationPrimaryHosts(applicationUrl: string): string[] {
  const applicationHost = extractHostname(applicationUrl);
  if (!applicationHost || isRegexPattern(applicationHost)) {
    return [];
  }

  const hosts = new Set<string>([applicationHost]);
  const primaryDomain = getDomain(applicationHost);
  if (primaryDomain) {
    hosts.add(primaryDomain.toLowerCase());
  }
  return [...hosts];
}

function isNoiseHostExcluded({
  noiseHost,
  exactEnabledHosts,
  applicationPrimaryHosts,
}: {
  noiseHost: string;
  exactEnabledHosts: Set<string>;
  applicationPrimaryHosts: string[];
}): boolean {
  const normalizedNoise = noiseHost.toLowerCase();

  if (exactEnabledHosts.has(normalizedNoise)) {
    return true;
  }

  return applicationPrimaryHosts.some((primaryHost) =>
    isHostExactlyOrSubdomainOf(primaryHost, normalizedNoise),
  );
}
